import { useContext, useState } from 'react';
import "./Feedback.scss";
import TextArea from '../../../components/Fields/TextArea';
import InputBox from '../../../components/Fields/Input Box/InputBox.jsx';
import axios from "axios";
import { toast } from "react-toastify";
import { AppContext } from '../../../context/AppContext.jsx';
import SuccessDialog from '../../../components/SuccessDialog.jsx';

const DynamicFeedbackInputFields = ({ title, fields, formType }) => {
    const { backendUrl } = useContext(AppContext);
    const [feedbackData, setFeedbackData] = useState({
        students: {},
        teachers: {},
        parents: {}
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [successDialogConfig, setSuccessDialogConfig] = useState({
        header: "",
        message: "",
        buttonLabel: "OK"
    });
    const eMailpattern = /^\S+@\S+\.\S+$/
    const [errors, setErrors] = useState({})

    const styles = {
        display: "flex",
        justifyContent: "end",
        flexDirection: "column",
        gap: "12px",
    };
    const inputStyles = {
        padding: "clamp(12px, 2vw, 16px)",
        paddingLeft: "clamp(14px, 2.5vw, 19px)",
        borderRadius: "5px",
        color: "black",
        fontWeight: "500",
        fontFamily: "var(--font-paragraph)"
    };
    const labelStyles = {
        color: "#116C4A",
        fontWeight: "700",
        fontFamily: "var(--font-label",
        fontSize: "var(--font-label-size)"
    };
    const textAreaStyles = {
        ...inputStyles,
        width: "100%",
        padding: "16px",
        minHeight: "120px",
        resize: "vertical",
    };

    const handleChange = (fieldName, value) => {
        setFeedbackData(prev => ({
            ...prev,
            [formType]: {
                ...prev[formType],
                [fieldName]: value
            }
        }));
        setErrors((prevErrors) => ({
            ...prevErrors,
            [fieldName]: ""
        }));
    };

    const validateForm = (feedbackPayload) => {
        const errors = {};
        const emailPattern = /^\S+@\S+\.\S+$/;

        switch (feedbackPayload.type) {
            case "Student":
                if (!feedbackPayload.name) errors.name = "Required";
                if (!feedbackPayload.department) errors.department = "Required";
                if (!feedbackPayload.email) {
                    errors.email = "Required";
                } else if (!emailPattern.test(feedbackPayload.email)) {
                    errors.email = "Invalid email format";
                }
                if (!feedbackPayload.feedback) errors.feedback = "Required";
                break;

            case "Teacher":
                if (!feedbackPayload.name) errors.name = "Required";
                if (!feedbackPayload.department) errors.department = "Required";
                if (!feedbackPayload.email) {
                    errors.email = "Required";
                } else if (!emailPattern.test(feedbackPayload.email)) {
                    errors.email = "Invalid email format";
                }
                if (!feedbackPayload.designation) errors.designation = "Required";
                if (!feedbackPayload.feedback) errors.feedback = "Required";
                break;

            case "Parent":
                if (!feedbackPayload.parentName) errors.parentName = "Required";
                if (!feedbackPayload.studentName) errors.studentName = "Required";
                if (!feedbackPayload.department) errors.department = "Required";
                if (!feedbackPayload.email) {
                    errors.email = "Required";
                } else if (!emailPattern.test(feedbackPayload.email)) {
                    errors.email = "Invalid email format";
                }
                if (!feedbackPayload.feedback) errors.feedback = "Required";
                break;

            default:
                errors.type = "Invalid feedback type";
        }

        setErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const clearForm = () => {
        setFeedbackData((prevState) => ({
            ...prevState,
            [formType]: {}
        }));
    }

    const getSuccessMessageConfig = (type) => {
        switch (type) {
            case "Student":
                return {
                    header: "Feedback Submitted",
                    message: "Student feedback submitted successfully!",
                    buttonLabel: "OK"
                };
            case "Teacher":
                return {
                    header: "Feedback Submitted",
                    message: "Teacher feedback submitted successfully!",
                    buttonLabel: "OK"
                };
            case "Parent":
                return {
                    header: "Feedback Submitted",
                    message: "Parent feedback submitted successfully!",
                    buttonLabel: "OK"
                };
            default:
                return {
                    header: "Success",
                    message: "Your feedback has been submitted successfully!",
                    buttonLabel: "OK"
                };
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        axios.defaults.withCredentials = true;
        const typeMapping = {
            students: "Student",
            teachers: "Teacher",
            parents: "Parent"
        };
        const filteredFeedback = Object.fromEntries(
            Object.entries(feedbackData[formType]).filter(([key, value]) => value?.trim())
        );
        const feedbackPayload = {
            ...filteredFeedback,
            type: typeMapping[formType]
        };
        try {
            if (validateForm(feedbackPayload)) {
                const res = await axios.post(`${backendUrl}/api/auth/submitFeedback`, feedbackPayload)
                if (res.data.success === true) {
                    const config = getSuccessMessageConfig(feedbackPayload.type);
                    setSuccessDialogConfig(config);
                    setShowSuccessDialog(true);
                    clearForm();
                }
            }
        }
        catch (error) {
            console.error('Error fetching users:', error);
            if (error.response && error.response.status === 503) {
                toast.error("⚠️ The service is currently under maintenance. Please try again later.");
            }
            else if (!navigator.onLine) {
                toast.error("🚫 No internet connection. Please check your network.");
            }
            else if (error.request && !error.response) {
                toast.error("⚠️ Network issue or server is unreachable. Try again later.")
            }
            else if (error.response && error.response.status === 500) {
                toast.error("Internal Server Error")
                clearForm();
            }
            else if (error.response?.data?.message === "Invalid email format") {
                toast.error("📧 Please enter a valid email address.");
            }
            else if (error.response && error.response.status === 400) {
                const missingFields = error.response.data?.missingFields || [];
                toast.error(`Missing required fields: ${missingFields.join(', ')}`);
            }
            else if (error.response?.status === 409) {
                toast.warn("⚠️ You've already submitted feedback.");
            }
            else {
                console.error("Unexpected error:", error);
                toast.error("Something went wrong.");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className="dynamicFeedbackInputFields">
            <header>
                <h2>{title}</h2>
            </header>
            <form onSubmit={(e) => handleSubmit(e)}>
                <div className="formContainer">
                    {fields.map((field, index) => (
                        field.type === "text" || field.type === "email" ? (
                            <div key={index} className="feedbackInputRows">
                                <InputBox
                                    fieldName={field.fieldName}
                                    fieldType={field.type}
                                    placeholder={field.placeholder}
                                    styles={styles}
                                    inputStyles={inputStyles}
                                    customLabelName={field.customLabelName}
                                    labelStyles={labelStyles}
                                    value={feedbackData[formType][field.fieldName] || ""}
                                    handleChange={(e) => handleChange(field.fieldName, e.target.value)}
                                    errors={errors}
                                    hasError={!!errors[field.fieldName]}
                                />
                                {errors[field.fieldName] && (
                                    <p className="error-message">{errors[field.fieldName]}</p>
                                )}
                            </div>
                        ) : (
                            <div key={index} className="fullWidth">
                                <TextArea
                                    styles={styles}
                                    inputStyles={textAreaStyles}
                                    labelStyles={labelStyles}
                                    fieldName={field.fieldName}
                                    placeholder={field.placeholder}
                                    value={feedbackData[formType][field.fieldName] || ""}
                                    handleChange={(e) => handleChange(field.fieldName, e.target.value)}
                                    hasError={!!errors[field.fieldName]}
                                />
                                {errors[field.fieldName] && (
                                    <p className="error-message">{errors[field.fieldName]}</p>
                                )}
                            </div>
                        )
                    ))}
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <button className="submitBtn" disabled={isSubmitting}>
                        {isSubmitting ? (
                            <>
                                <i className="pi pi-spin pi-spinner" style={{ marginRight: '8px' }}></i>
                                Submitting...
                            </>
                        ) : (
                            "Submit"
                        )}
                    </button>

                </div>
            </form>

            <SuccessDialog
                visible={showSuccessDialog}
                onHide={() => setShowSuccessDialog(false)}
                header={successDialogConfig.header}
                message={successDialogConfig.message}
                buttonLabel={successDialogConfig.buttonLabel}
            />
        </section>
    );
};

export default DynamicFeedbackInputFields;